import { useSecuritiesReadState } from '@/modules/projects/hooks/project'
import { AccountsTable } from './AccountsTable'
import { AllAccountsCard } from './AllAccountsCard'
import { LedgerIncompleteNotice } from './detail/LedgerIncompleteNotice'
import { PortfolioDetail } from './detail/PortfolioDetail'
import { accountsAsOf } from './model/portfolioOverview'
import type { MonthKey } from './model/types'
import { PortfolioHeading } from './PortfolioHeading'
import { PortfolioSkeleton } from './PortfolioSkeleton'
import { SecuritiesFailedState } from './SecuritiesFailedState'
import { SecuritiesPendingState } from './SecuritiesPendingState'
import { ShowingSelector } from './ShowingSelector'
import { UploadStatementsButton } from './UploadStatementsButton'
import {
  type PortfolioOverviewWithLedger,
  usePortfolioOverview,
} from './usePortfolioOverview'

export interface PortfolioSelection {
  // Null means the latest statement month.
  month: MonthKey | null
  onMonthChange: (month: MonthKey) => void
  // Null means All accounts.
  selectedAccountId: string | null
  onSelectAccount: (accountId: string | null) => void
}

const NoPortfolio = () => {
  const securitiesReadState = useSecuritiesReadState()
  if (securitiesReadState === 'reading') {
    return (
      <SecuritiesPendingState
        layout="panel"
        detail="The system is calculating and verifying positions, activity, and realized gains. This usually takes up to 24 hours. They appear here when it's done."
      />
    )
  }
  if (securitiesReadState === 'failed') {
    return <SecuritiesFailedState layout="panel" />
  }
  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border bg-white p-10 text-center">
      <div className="space-y-1">
        <p className="text-base font-medium text-blue-7">
          No brokerage statements in this project
        </p>
        <p className="mx-auto max-w-md text-sm text-ink-subtle">
          Upload an investment or retirement statement to see its accounts,
          holdings, activity and realized gains here.
        </p>
      </div>
      <UploadStatementsButton label="Upload statements" />
    </div>
  )
}

const PortfolioScreen = ({
  overview,
  month,
  selection,
}: {
  overview: PortfolioOverviewWithLedger
  month: MonthKey
  selection: PortfolioSelection
}) => {
  const asOf = accountsAsOf(overview, month)
  const isKnownAccount = asOf.accounts.some(
    (account) => account.accountId === selection.selectedAccountId,
  )
  // One account is already the whole view (V4), so there is nothing to choose and no selector.
  const canChoose = asOf.accounts.length > 1
  const selectedAccountId =
    canChoose && isKnownAccount ? selection.selectedAccountId : null

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PortfolioHeading
        asOf={{
          months: overview.months,
          month,
          onMonthChange: selection.onMonthChange,
        }}
      />
      <div>
        <LedgerIncompleteNotice />
        <AllAccountsCard
          overview={overview}
          asOf={asOf}
          isLedgerIncomplete={overview.ledgerIncomplete != null}
        />
      </div>
      <AccountsTable
        asOf={asOf}
        selectedAccountId={selectedAccountId}
        onSelectAccount={selection.onSelectAccount}
      />
      {canChoose && (
        <ShowingSelector
          asOf={asOf}
          selectedAccountId={selectedAccountId}
          onSelectAccount={selection.onSelectAccount}
        />
      )}
      <PortfolioDetail
        overview={overview}
        month={month}
        selectedAccountId={selectedAccountId}
      />
    </div>
  )
}

// The Portfolio tab. It never blanks: it holds its own shape while statements and the ledger
// load, then a read in progress, a failed read, or no brokerage statements each get a state.
export const PortfolioPanel = (selection: PortfolioSelection) => {
  const overview = usePortfolioOverview()

  if (!overview) return <PortfolioSkeleton />
  const latestMonth = overview.latestMonth
  if (overview.timelines.length === 0 || !latestMonth) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <PortfolioHeading />
        <NoPortfolio />
      </div>
    )
  }
  const month =
    selection.month && overview.months.includes(selection.month)
      ? selection.month
      : latestMonth
  return (
    <PortfolioScreen overview={overview} month={month} selection={selection} />
  )
}
